/**
 * SY-03 soft permission ask, as pure rules plus a little device-local memory (eager and tiny:
 * the manifest's mapDue uses it, the lazy chunk shares it).
 *
 * The game asks with its own pre-prompt before the OS ever does, and only:
 *  - after a positive moment in this session: a new win (lifetime stars went up since the last
 *    map visit) or offline sales just collected (Flow D, OVL-01);
 *  - never in the first session, never before Flow A is done, never in COPPA mode;
 *  - never once the OS said no (or cannot show notifications at all);
 *  - at most once every 7 days, and at most 3 times in all ("Not now" is always fine).
 * A permission the OS granted without the game asking (Android 12 and older grant it at install;
 * a browser may have allowed the site before) is not a yes from the player: such a device is
 * never registered until the player opts in, through this same ask or the screen's "Turn on"
 * (neither shows the OS prompt then). The yes belongs to the account that gave it: on a shared
 * device, another account signed in later (a child's COPPA account included) is not registered
 * on it, and follows the ask rules itself. Once the signed-in account opted in and the
 * permission is granted, the map visit instead keeps this device's registration fresh: once a
 * week (web push subscriptions and tokens rotate), right away when the device was registered
 * for another account since, and the UTC offset again when the device clock moved (DST).
 * A blocked permission is read again once per app run (and daily in a long one): the player may
 * allow notifications in the system settings without ever opening the game's own screen. On the
 * web the browser's synchronous `Notification.permission` rules that out first, so a blocked
 * site does not load this feature's chunk on every start. A device that cannot show
 * notifications at all is read again once a week (an app or browser update may add it).
 */
import type { PushPermission } from '@mp/adapters/push';
import type { AppContext } from '../../game/context.js';
import { currentStep } from '../../services/firstSession.js';
import type { KV } from '../../services/storage.js';

const DAY = 86_400_000;
const FEATURE = 'notifications';

export const ASK = {
  /** at most one soft ask per 7 days */
  gapMs: 7 * DAY,
  /** soft asks shown in all */
  max: 3,
  /** a player this new on their first recorded run is in their first session */
  freshMs: DAY,
  /** re-register a granted device this often */
  refreshMs: 7 * DAY,
  /** read a blocked permission again this often within one long app run */
  recheckMs: DAY,
  /** read an unsupported device again this often (not every app run) */
  unsupportedMs: 7 * DAY,
} as const;

export type Moment = 'win' | 'offline';
/**
 * `ask`: the soft ask. `refresh`: register this (granted) device again. `tz`: send the device's
 * new UTC offset. `recheck`: read a blocked permission again (it may be allowed by now).
 */
export type Decision = 'ask' | 'refresh' | 'tz' | 'recheck';

/** What this device remembers (services.kv, key `notifications`). */
export interface NotifLocal {
  /** last soft ask shown (epoch ms) */
  askedAt: number | null;
  /** soft asks shown so far */
  asks: number;
  /**
   * The accounts (player ids) that said yes in the game on this device (soft ask or "Turn on").
   * Only for them is a granted device registered. `''`: a yes given before any account was
   * signed in on this device, taken by the account that registers next. A device registered
   * before the opt-in was remembered counts as a yes from the account it was registered for.
   */
  optedFor: string[];
  /** OS permission as last read */
  perm: PushPermission | null;
  /** when the OS permission was last read (epoch ms) */
  permAt: number | null;
  /** last successful POST /v1/push/register (or native token registration) */
  regAt: number | null;
  /** the account (player id) that registration went to */
  regFor: string | null;
  /** the UTC offset (minutes east) last sent with PUT /v1/notifications/prefs */
  tz: number | null;
  /** app runs that reached the map hooks while the feature was on */
  runs: number;
}

const KEY = 'notifications';
const EMPTY: NotifLocal = { askedAt: null, asks: 0, optedFor: [], perm: null, permAt: null, regAt: null, regFor: null, tz: null, runs: 0 };
const PERMS: readonly PushPermission[] = ['granted', 'denied', 'prompt', 'unsupported'];

const numOr = <T>(v: unknown, d: T): number | T => (typeof v === 'number' && Number.isFinite(v) ? v : d);
const strOr = <T>(v: unknown, d: T): string | T => (typeof v === 'string' && v !== '' ? v : d);

export function readLocal(kv: KV): NotifLocal {
  try {
    const raw = JSON.parse(kv.get(KEY) ?? 'null') as Partial<Record<keyof NotifLocal, unknown>> | null;
    if (!raw || typeof raw !== 'object') return { ...EMPTY };
    const regAt = numOr(raw.regAt, null);
    const regFor = strOr(raw.regFor, null);
    // before the yes was kept per account: a device-wide yes (or a registration) was its account's
    const legacy = (raw as { optedIn?: unknown }).optedIn === true || regAt !== null ? [regFor ?? ''] : [];
    const optedFor = Array.isArray(raw.optedFor) ? [...new Set(raw.optedFor.filter((x): x is string => typeof x === 'string'))] : legacy;
    return {
      askedAt: numOr(raw.askedAt, null),
      asks: numOr(raw.asks, 0),
      optedFor,
      perm: PERMS.includes(raw.perm as PushPermission) ? (raw.perm as PushPermission) : null,
      permAt: numOr(raw.permAt, null),
      regAt,
      regFor,
      tz: numOr(raw.tz, null),
      runs: numOr(raw.runs, 0),
    };
  } catch {
    return { ...EMPTY };
  }
}

export function writeLocal(kv: KV, patch: Partial<NotifLocal>): NotifLocal {
  const next = { ...readLocal(kv), ...patch };
  try {
    kv.set(KEY, JSON.stringify(next));
  } catch {
    /* storage full or blocked: the rules fall back to asking less, never more */
  }
  return next;
}

/** The signed-in account (null before the first sign-in) said yes on this device. */
export function optedIn(l: Pick<NotifLocal, 'optedFor'>, player: string | null): boolean {
  return l.optedFor.includes(player ?? '') || l.optedFor.includes('');
}

/** Remember this account's yes (before the OS prompt: a later "allow" in the settings still counts). */
export function optIn(kv: KV, player: string | null): NotifLocal {
  const l = readLocal(kv);
  return optedIn(l, player) ? l : writeLocal(kv, { optedFor: [...l.optedFor, player ?? ''] });
}

/** Minutes east of UTC, as the API stores it (-720…840). */
export function tzOffsetMin(now: number): number {
  const off = -new Date(now).getTimezoneOffset() || 0; // never -0 (UTC)
  return Math.max(-720, Math.min(840, Math.round(off)));
}

export interface DecideInput {
  now: number;
  local: NotifLocal;
  /** the first app session of a brand-new player */
  firstSession: boolean;
  moment: Moment | null;
  /** Flow A done (no first-session prompts left) */
  tutorialDone: boolean;
  coppa: boolean;
  /** the signed-in account (null before the first guest sign-in) */
  player: string | null;
  /** the device's UTC offset now */
  tz: number;
  /** the hook already ran in this app run, for this account */
  checked: boolean;
  /** a cheap synchronous read says the permission is still blocked (the web's Notification.permission) */
  blockedNow?: boolean;
}

/**
 * Whether this map visit should soft-ask, refresh the registration, re-read the permission, or do
 * nothing. A granted permission the signed-in account never said yes to follows the ask rules
 * (so a COPPA account on a device someone else opted in is never registered).
 */
export function decide(i: DecideInput): Decision | null {
  const { local } = i;
  if (local.perm === 'granted' && optedIn(local, i.player)) {
    if (i.checked) return null;
    const otherAccount = i.player !== null && local.regFor !== i.player;
    if (local.regAt === null || i.now - local.regAt >= ASK.refreshMs || otherAccount) return 'refresh';
    return local.tz === i.tz ? null : 'tz';
  }
  if (local.perm === 'unsupported') return local.permAt === null || i.now - local.permAt >= ASK.unsupportedMs ? 'recheck' : null;
  if (local.perm === 'denied') {
    // never asked again, but read again: allowed in the system settings, the device gets registered
    if (i.blockedNow) return null;
    return !i.checked || local.permAt === null || i.now - local.permAt >= ASK.recheckMs ? 'recheck' : null;
  }
  if (i.checked || i.firstSession || !i.tutorialDone || i.coppa || i.moment === null) return null;
  if (local.asks >= ASK.max) return null;
  if (local.askedAt !== null && i.now - local.askedAt < ASK.gapMs) return null;
  return 'ask';
}

/**
 * Whether this build can deliver notifications on this device at all. The `none` provider
 * cannot; the dev provider grants itself and delivers nothing, so it only counts in dev and e2e
 * runs (a release web build without a push provider falls back to it).
 */
export function pushAvailable(a: AppContext): boolean {
  const kind = a.services.providers.push.kind;
  return kind !== 'none' && (kind !== 'dev' || a.flags.dev || a.flags.e2e);
}

/** The signed-in account, if any. */
export const playerOf = (a: AppContext): string | null => a.services.store.state.auth?.playerId ?? null;

/** The browser still blocks notifications for this site (synchronous; false where it cannot tell, e.g. the app). */
export function blockedNow(a: AppContext): boolean {
  if (a.services.providers.push.kind !== 'web') return false;
  try {
    return typeof Notification !== 'undefined' && Notification.permission === 'denied';
  } catch {
    return false;
  }
}

/** Per app run (keyed by the session object): first session?, the stars at the last map visit, the pending decision. */
interface Run {
  first: boolean;
  stars: number;
  moment: Moment | null;
  decision: Decision | null;
  /** the account the hook last ran for in this run (an account switch runs it once more) */
  ranFor: string | null;
}
const runs = new WeakMap<object, Run>();

/** This run's memory; the first call of a run counts it. Every call is one map visit (the win baseline moves). */
function visit(a: AppContext): Run {
  const s = a.services.store.state;
  const offline = a.session.offlineShown && a.session.offlineCoins > 0;
  let run = runs.get(a.session);
  if (!run) {
    const local = writeLocal(a.services.kv, { runs: readLocal(a.services.kv).runs + 1 });
    run = { first: local.runs <= 1 && a.services.now() - s.createdAt < ASK.freshMs, stars: s.stars, moment: offline ? 'offline' : null, decision: null, ranFor: null };
    runs.set(a.session, run);
    return run;
  }
  const won = s.stars > run.stars;
  run.stars = s.stars;
  run.moment = won ? 'win' : offline ? 'offline' : null;
  return run;
}

/** The hook already ran in this app run for the signed-in account (a first guest sign-in is not a switch). */
export function ranThisRun(a: AppContext): boolean {
  if (!a.session.checked.has(FEATURE)) return false;
  const ranFor = runs.get(a.session)?.ranFor ?? null;
  return ranFor === null || ranFor === playerOf(a);
}

/** onMapShow: the hook ran now, for this account. */
export function markRan(a: AppContext): void {
  a.session.checked.add(FEATURE);
  const run = runs.get(a.session);
  if (run) run.ranFor = playerOf(a);
}

/** The inputs for `decide` from the running app (local memory may be overridden, e.g. with a fresh permission). */
export function inputs(a: AppContext, local: NotifLocal = readLocal(a.services.kv), checked: boolean = ranThisRun(a)): DecideInput {
  const run = runs.get(a.session);
  const s = a.services.store.state;
  const now = a.services.now();
  return {
    now,
    local,
    firstSession: run?.first ?? true,
    moment: run?.moment ?? null,
    tutorialDone: currentStep(s) === 'done',
    coppa: s.coppa,
    player: playerOf(a),
    tz: tzOffsetMin(now),
    checked,
    blockedNow: local.perm === 'denied' && blockedNow(a),
  };
}

/** Manifest mapDue: cheap and synchronous (kv reads only). */
export function mapDue(a: AppContext): boolean {
  const run = visit(a);
  run.decision = null;
  if (!pushAvailable(a) || a.services.sync.status === 'offline') return false;
  run.decision = decide(inputs(a));
  return run.decision !== null;
}

/** The moment this run's map visit found (for the ask's wording). */
export function momentOf(a: AppContext): Moment | null {
  return runs.get(a.session)?.moment ?? null;
}
